/**
 * 文档状态管理
 * 处理文档状态的定义和管理
 *
 * 重要: 在使用此模块前，请先阅读文档ID生命周期指南，了解ID与状态同步的最佳实践:
 * @see ../DOCUMENT_ID_LIFECYCLE.md
 *
 * 核心功能：
 * 1. 定义文档状态结构
 * 2. 提供状态更新方法
 * 3. 与DocumentManager交互
 * 4. 管理状态变化的副作用
 */
import { useState, useCallback, useEffect } from 'react';
import { DocumentIdentitySource, } from '@readsavor/types';
import { isEqual } from 'lodash-es';
import { DocumentManager } from '../services/DocumentManager.js';
import { PdfManager } from '../services/PdfManager.js';
import { DocumentIdentityService } from '../services/DocumentIdentityService.js';
import { DocumentSynchronizer } from '../services/DocumentSynchronizer.js';
/**
 * 创建文档状态管理器
 * 提供文档状态和相关操作方法
 */
export function useDocumentState() {
    const textDocumentManager = DocumentManager.getInstance();
    const pdfDocumentManager = PdfManager.getInstance();
    const [documentState, setDocumentState] = useState({
        contentType: undefined,
    });
    useEffect(() => {
        const synchronizer = DocumentSynchronizer.getInstance();
        const unsubscribe = synchronizer.subscribeToContentUpdates((docId, contentType, newStateFromEvent) => {
            if (docId === -1) {
                setDocumentState(prevState => {
                    if (prevState.contentType !== undefined) {
                        return { contentType: undefined };
                    }
                    return prevState;
                });
                return;
            }
            setDocumentState(prevState => {
                let newDocumentStateSnapshot;
                const currentIdentity = prevState.identity;
                const currentDocIsText = prevState.contentType === 'text';
                const currentDocIsPdf = prevState.contentType === 'pdf';
                const isSameDoc = currentIdentity && currentIdentity.id === docId && currentIdentity.type === contentType;
                if (contentType === 'text') {
                    const partialTextState = newStateFromEvent;
                    const newIdentity = isSameDoc
                        ? currentIdentity
                        : { id: docId, type: 'text' };
                    const baseState = isSameDoc && currentDocIsText ? prevState : undefined;
                    newDocumentStateSnapshot = {
                        identity: newIdentity,
                        contentType: 'text',
                        title: partialTextState.title ?? baseState?.title,
                        language: partialTextState.language ?? baseState?.language ?? '',
                        targetLanguage: partialTextState.targetLanguage ?? baseState?.targetLanguage ?? '',
                    };
                }
                else if (contentType === 'pdf') {
                    const partialPdfState = newStateFromEvent;
                    const newIdentity = isSameDoc
                        ? currentIdentity
                        : { id: docId, type: 'pdf' };
                    const baseState = isSameDoc && currentDocIsPdf ? prevState : undefined;
                    newDocumentStateSnapshot = {
                        identity: newIdentity,
                        contentType: 'pdf',
                        title: partialPdfState.title ?? baseState?.title,
                        language: partialPdfState.language ?? baseState?.language ?? '',
                        targetLanguage: partialPdfState.targetLanguage ?? baseState?.targetLanguage ?? '',
                    };
                }
                else {
                    // If the content type from the event is unknown, but it's for the same document,
                    // DO NOT change the state. This is likely a progress update or other partial event.
                    if (isSameDoc) {
                        return prevState;
                    }
                    console.warn('[DOCUMENT_STATE] Received content update with unexpected contentType for non-reset event', { docId, contentType });
                    if (prevState.contentType !== undefined) {
                        return { contentType: undefined };
                    }
                    return prevState;
                }
                // Final check: Only return a new state object if it's actually different.
                return isEqual(prevState, newDocumentStateSnapshot)
                    ? prevState
                    : newDocumentStateSnapshot;
            });
        });
        return () => {
            unsubscribe();
        };
    }, []);
    const processContent = useCallback(async (text) => {
        try {
            return await textDocumentManager.processContent(text);
        }
        catch (error) {
            console.error('[DOCUMENT_STATE] Failed to process content', { error });
            throw new Error('Failed to process content');
        }
    }, [textDocumentManager]);
    const storeTextDocument = useCallback(async (text, options) => {
        const documentIdentity = await textDocumentManager.storeDocument(text, options);
        const synchronizer = DocumentSynchronizer.getInstance();
        const contentStateForSync = {
            title: options.title,
            language: options.language,
            targetLanguage: options.targetLanguage,
        };
        const success = synchronizer.syncDocumentActivation(documentIdentity, contentStateForSync, DocumentIdentitySource.STORAGE);
        if (!success) {
            console.error('[DOCUMENT_STATE] Failed to synchronize document activation after storing text document', {
                identity: documentIdentity,
            });
            throw new Error('Failed to synchronize document activation for text document');
        }
        return documentIdentity;
    }, [textDocumentManager]);
    const storePdfDocument = useCallback(async (file, arrayBuffer, options) => {
        const documentIdentity = await pdfDocumentManager.storeDocument(file, arrayBuffer, options);
        const synchronizer = DocumentSynchronizer.getInstance();
        const contentStateForSync = {
            title: options.title || file.name,
            language: options.language,
            targetLanguage: options.targetLanguage,
        };
        const success = synchronizer.syncDocumentActivation(documentIdentity, contentStateForSync, DocumentIdentitySource.STORAGE);
        if (!success) {
            console.error('[DOCUMENT_STATE] Failed to synchronize document activation after storing PDF document', {
                identity: documentIdentity,
            });
            throw new Error('Failed to synchronize document activation for PDF document');
        }
        return documentIdentity;
    }, [pdfDocumentManager]);
    const loadDocument = useCallback(async (identity) => {
        setDocumentState({ contentType: undefined, identity, error: undefined }); // Indicate loading
        try {
            let documentResult;
            let contentStateForSync;
            if (identity.type === 'text') {
                documentResult = await textDocumentManager.loadDocument(identity);
                const textResult = documentResult;
                contentStateForSync = {
                    title: textResult.title,
                    language: textResult.language,
                    targetLanguage: textResult.targetLanguage,
                };
                setDocumentState({
                    identity: textResult.identity,
                    contentType: 'text',
                    title: textResult.title,
                    language: textResult.language,
                    targetLanguage: textResult.targetLanguage,
                });
            }
            else if (identity.type === 'pdf') {
                documentResult = await pdfDocumentManager.loadDocument(identity);
                const pdfResult = documentResult;
                contentStateForSync = {
                    // Ensure only fields compatible with Partial<ActivePdfDocumentState>
                    title: pdfResult.title,
                    language: pdfResult.language || '', // Ensure language is not undefined
                    targetLanguage: pdfResult.targetLanguage,
                };
                // Update documentState specific to PDF
                setDocumentState(prevState => {
                    // Check if it's still the same document being loaded to avoid race conditions
                    if (prevState.identity?.id === identity.id &&
                        prevState.identity?.type === identity.type) {
                        const newState = {
                            identity: pdfResult.identity,
                            contentType: 'pdf',
                            title: pdfResult.title,
                            language: pdfResult.language || '', // Ensure language is not undefined
                            targetLanguage: pdfResult.targetLanguage,
                        };
                        return newState;
                    }
                    return prevState; // If identity changed, don't update with stale data
                });
            }
            else {
                const errorMsg = `Unsupported document type: ${identity.type}`;
                console.error(errorMsg);
                setDocumentState({ contentType: undefined, identity, error: errorMsg });
                throw new Error(errorMsg);
            }
            const identityService = DocumentIdentityService.getInstance();
            const currentServiceState = identityService.state;
            if (currentServiceState.currentIdentity &&
                currentServiceState.currentIdentity.id === identity.id &&
                currentServiceState.currentIdentity.type === identity.type &&
                currentServiceState.documentSource === DocumentIdentitySource.STORAGE) {
                if (identity.type === 'text') {
                    const res = documentResult;
                    setDocumentState({
                        identity: res.identity,
                        contentType: 'text',
                        title: res.title,
                        language: res.language,
                        targetLanguage: res.targetLanguage,
                    });
                }
                else if (identity.type === 'pdf') {
                    const res = documentResult;
                    setDocumentState({
                        identity: res.identity,
                        contentType: 'pdf',
                        title: res.title,
                        language: res.language || '',
                        targetLanguage: res.targetLanguage,
                    });
                }
            }
            else {
                const synchronizer = DocumentSynchronizer.getInstance();
                const success = synchronizer.syncDocumentActivation(identity, contentStateForSync, DocumentIdentitySource.STORAGE);
                if (!success) {
                    const syncErrorMsg = 'Failed to synchronize document activation after load';
                    console.error('[DOCUMENT_STATE] ' + syncErrorMsg, { identity });
                    setDocumentState({
                        contentType: undefined,
                        identity: identity,
                        error: syncErrorMsg,
                    });
                    throw new Error(syncErrorMsg);
                }
            }
            return documentResult;
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            console.error('[DOCUMENT_STATE] Error during loadDocument execution:', {
                identity,
                error: errorMessage,
            });
            setDocumentState({
                contentType: undefined,
                identity: identity,
                error: errorMessage,
            });
            throw error;
        }
    }, [textDocumentManager, pdfDocumentManager, setDocumentState]);
    const resetDocumentState = useCallback(() => {
        const identityService = DocumentIdentityService.getInstance();
        const currentIdentity = identityService.state.currentIdentity;
        if (currentIdentity) {
            const synchronizer = DocumentSynchronizer.getInstance();
            synchronizer.syncDocumentDeactivation(currentIdentity);
        }
        else {
            setDocumentState({ contentType: undefined });
        }
    }, []);
    const deleteDocument = useCallback(async (identity) => {
        try {
            if (identity.type === 'text') {
                await textDocumentManager.deleteDocument(identity);
            }
            else if (identity.type === 'pdf') {
                await pdfDocumentManager.deleteDocument(identity);
            }
            else {
                const err = new Error('Invalid contentType for deleteDocument from identity object');
                console.warn('[DOCUMENT_STATE] ' + err.message, { identity });
                throw err;
            }
            const synchronizer = DocumentSynchronizer.getInstance();
            const success = synchronizer.syncDocumentDeactivation(identity);
            if (success) {
                /* empty */
            }
            else {
                console.warn(`[DOCUMENT_STATE] Document (id=${identity.id}, type=${identity.type}) deleted, but failed to sync deactivation. Manual check might be needed.`, { identity });
            }
        }
        catch (error) {
            console.error(`[DOCUMENT_STATE] Failed to delete document (id=${identity.id}, type=${identity.type})`, {
                identity,
                error,
            });
            throw error;
        }
    }, [textDocumentManager, pdfDocumentManager]);
    return {
        documentState,
        storeTextDocument,
        storePdfDocument,
        loadDocument,
        resetDocumentState,
        processContent,
        deleteDocument,
    };
}
//# sourceMappingURL=DocumentState.js.map